import React, { createContext, useContext, useState, useEffect } from 'react';

const PricingContext = createContext({
  country: 'Sri Lanka',
  countryCode: 'LK',
  currencyCode: 'LKR',
  currencySymbol: 'Rs.',
  exchangeRate: 1,
  loading: true
});

// Map country codes to their currency
const CURRENCY_MAP = {
  LK: { code: 'LKR', symbol: 'Rs.' },
  US: { code: 'USD', symbol: '$' },
  GB: { code: 'GBP', symbol: '£' },
  AU: { code: 'AUD', symbol: 'A$' },
  CA: { code: 'CAD', symbol: 'C$' },
  DE: { code: 'EUR', symbol: '€' },
  FR: { code: 'EUR', symbol: '€' },
  IT: { code: 'EUR', symbol: '€' },
  ES: { code: 'EUR', symbol: '€' },
  NL: { code: 'EUR', symbol: '€' },
  BE: { code: 'EUR', symbol: '€' },
  AT: { code: 'EUR', symbol: '€' },
  PT: { code: 'EUR', symbol: '€' },
  IE: { code: 'EUR', symbol: '€' },
  FI: { code: 'EUR', symbol: '€' },
  GR: { code: 'EUR', symbol: '€' },
  IN: { code: 'INR', symbol: '₹' },
  JP: { code: 'JPY', symbol: '¥' },
  CN: { code: 'CNY', symbol: '¥' },
  SG: { code: 'SGD', symbol: 'S$' },
  MY: { code: 'MYR', symbol: 'RM' },
  AE: { code: 'AED', symbol: 'AED ' },
  SA: { code: 'SAR', symbol: 'SAR ' },
  QA: { code: 'QAR', symbol: 'QAR ' },
  KW: { code: 'KWD', symbol: 'KWD ' },
  NZ: { code: 'NZD', symbol: 'NZ$' },
};

// Fallback rates: how much 1 LKR is worth in other currencies (approximate)
const FALLBACK_RATES = {
  USD: 0.0033, GBP: 0.0026, EUR: 0.0031, AUD: 0.0052,
  CAD: 0.0046, INR: 0.28, JPY: 0.50, CNY: 0.024,
  SGD: 0.0045, MYR: 0.015, AED: 0.012, SAR: 0.013,
  QAR: 0.012, KWD: 0.001, NZD: 0.0057
};

export const PricingProvider = ({ children }) => {
  const [pricingData, setPricingData] = useState({
    country: 'Sri Lanka',
    countryCode: 'LK',
    currencyCode: 'LKR',
    currencySymbol: 'Rs.',
    exchangeRate: 1,
    loading: true
  });

  useEffect(() => {
    const detectLocationAndCurrency = async () => {
      try {
        const response = await fetch('/api/geo/detect');
        const data = await response.json();

        const countryCode = data.country_code || 'LK';
        const currencyInfo = CURRENCY_MAP[countryCode] || { code: 'USD', symbol: '$' };

        // Sri Lanka — no conversion needed
        if (countryCode === 'LK') {
          setPricingData({
            country: data.country || 'Sri Lanka',
            countryCode: 'LK',
            currencyCode: 'LKR',
            currencySymbol: 'Rs.',
            exchangeRate: 1,
            loading: false
          });
          return;
        }

        // Try to fetch live exchange rate (LKR → user's currency)
        let exchangeRate = FALLBACK_RATES[currencyInfo.code] || 0.0033;
        try {
          const rateRes = await fetch(`https://open.er-api.com/v6/latest/LKR`);
          const rateData = await rateRes.json();
          if (rateData.rates && rateData.rates[currencyInfo.code]) {
            exchangeRate = rateData.rates[currencyInfo.code];
          }
        } catch {
          // Use fallback rates silently
        }

        setPricingData({
          country: data.country || 'International',
          countryCode,
          currencyCode: currencyInfo.code,
          currencySymbol: currencyInfo.symbol,
          exchangeRate,
          loading: false
        });
      } catch (error) {
        console.error('Geo/currency detection failed:', error);
        setPricingData({
          country: 'Sri Lanka',
          countryCode: 'LK',
          currencyCode: 'LKR',
          currencySymbol: 'Rs.',
          exchangeRate: 1,
          loading: false
        });
      }
    };

    detectLocationAndCurrency();
  }, []);

  return (
    <PricingContext.Provider value={pricingData}>
      {children}
    </PricingContext.Provider>
  );
};

export const usePricing = () => useContext(PricingContext);

export const formatPrice = (amountLKR, pricing) => {
  // Default to LKR if no pricing context or if in Sri Lanka
  if (!pricing || pricing.countryCode === 'LK' || !pricing.exchangeRate || pricing.exchangeRate === 1) {
    return `Rs. ${amountLKR.toLocaleString()}`;
  }

  const converted = Math.round(amountLKR * pricing.exchangeRate);
  return `${pricing.currencySymbol}${converted.toLocaleString()}`;
};

export const getDisplayPrices = (subject, pricing) => {
  if (!subject) return null;

  const isLocal = !pricing || pricing.countryCode === 'LK';

  if (isLocal) {
    return {
      group: subject.group_price_local || 2500,
      individualPhysical: subject.individual_price_physical_local || 4000,
      individualOnline: subject.individual_price_online_local || 3500,
      isLocal: true,
    };
  }

  return {
    group: subject.group_price_international || 4000,
    individual: subject.individual_price_international || 6000,
    isLocal: false,
  };
};
